import { motion, AnimatePresence } from 'framer-motion'

/**
 * Live "negativity fog" — an edge-only red vignette that frames the page and
 * intensifies (stronger + larger, breathing faster) as the viewer's tone trends
 * negative. Driven by Tone Engine V2's negativityIntensity (0..1).
 *
 * Design constraints (deliberate):
 *  - Fixed, full-viewport, but pointer-events-none → never blocks any control.
 *  - Painted ONLY near the edges via a radial gradient whose transparent center
 *    grows/shrinks with intensity, so it never washes out the middle of the page
 *    where the key elements live. It is a border fog, not a full-screen tint.
 *  - Hidden entirely below a threshold so mild/transient negativity stays quiet;
 *    it appears when there is a sustained PATTERN of negativity.
 */

// Below this, render nothing — we only surface a sustained pattern.
const SHOW_THRESHOLD = 0.22

function clamp01(n) {
  if (!Number.isFinite(n)) return 0
  return Math.min(1, Math.max(0, n))
}

export default function ToneFogOverlay({ intensity = 0 }) {
  const raw = clamp01(intensity)
  const visible = raw >= SHOW_THRESHOLD

  // Eased 0..1 above the threshold, so the fog ramps from "just appeared" to full.
  const e = visible ? clamp01((raw - SHOW_THRESHOLD) / (1 - SHOW_THRESHOLD)) : 0

  // Fog grows INWARD as it intensifies: the transparent center shrinks from 74%
  // to 48% of the frame, and the red edge gets more opaque.
  const transparentStop = 74 - 26 * e
  const edgeAlpha = (0.10 + 0.34 * e).toFixed(3)
  const midAlpha = (0.0 + 0.10 * e).toFixed(3)

  // A soft inset bloom hugging the very edge, on top of the gradient.
  const bloomBlur = Math.round(60 + 130 * e)
  const bloomSpread = Math.round(4 + 26 * e)
  const bloomAlpha = (0.10 + 0.40 * e).toFixed(3)

  // Breathing: faster + deeper pulse the more negative it gets, to draw the eye.
  const pulsePeriod = 3.4 - 1.6 * e // seconds
  const pulseFloor = 1 - (0.18 + 0.22 * e)

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 z-40"
          initial={{ opacity: 0 }}
          animate={{ opacity: [1, pulseFloor, 1] }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: {
              duration: pulsePeriod,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
          style={{
            background: `radial-gradient(ellipse at center, rgba(220,38,38,${midAlpha}) ${transparentStop}%, rgba(220,38,38,${edgeAlpha}) 100%)`,
            boxShadow: `inset 0 0 ${bloomBlur}px ${bloomSpread}px rgba(220,38,38,${bloomAlpha})`,
            transition: 'background 700ms ease, box-shadow 700ms ease',
          }}
        />
      ) : null}
    </AnimatePresence>
  )
}
